import { WidgetDataParams, baseApi, createProjectCacheKey } from './api';
import { ApiResponse } from '../types';
import type {
  Dashboard,
  DashboardWidget,
  CreateDashboardRequest,
  UpdateDashboardRequest,
  CreateWidgetRequest,
  UpdateWidgetRequest,
  UpdateLayoutRequest,
  SetDefaultDashboardRequest,
  GetDashboardsParams,
} from '../types/dashboard';

// Dashboard widget data types
export interface WidgetData {
  [key: string]: any;
}

export interface DashboardStats {
  totalProjects: number;
  activeProjects: number;
  totalRevenue: number;
  totalCosts: number;
  openTasks: number;
  overdueTasks: number;
}

export interface ProjectDashboardStats {
  projectValue: number;
  spentToDate: number;
  remainingBudget: number;
  completionPercentage: number;
  openTasks: number;
  overdueTasks: number;
  teamMembers: number;
  activeMilestones: number;
}

export interface BudgetSummary {
  totalBudget: number;
  revisedBudget: number;
  committed: number;
  actual: number;
  forecast: number;
  variance: number;
  variancePercent: number;
  byCategory: Array<{
    category: string;
    budget: number;
    actual: number;
    variance: number;
  }>;
}

export interface ScheduleSummary {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  notStartedTasks: number;
  overdueTasks: number;
  completionPercentage: number;
  daysRemaining: number;
  criticalPathTasks: number;
  floatDays: number;
}

export interface TaskSummary {
  total: number;
  byStatus: {
    pending: number;
    in_progress: number;
    completed: number;
    on_hold: number;
  };
  byPriority: {
    high: number;
    medium: number;
    low: number;
  };
  overdue: number;
  dueThisWeek: number;
}

export interface TeamSummary {
  totalMembers: number;
  activeMembers: number;
  byRole: Array<{ role: string; count: number }>;
  topPerformers: Array<{
    id: string;
    name: string;
    avatar?: string;
    tasksCompleted: number;
    efficiency: number;
  }>;
}

export interface DocumentSummary {
  totalDocuments: number;
  recentUploads: number;
  byType: Array<{ type: string; count: number }>;
  pendingReview: number;
  approved: number;
  rejected: number;
}

export interface SafetySummary {
  totalIncidents: number;
  openIncidents: number;
  resolvedIncidents: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  inspectionsDue: number;
  safetyScore: number;
  daysWithoutIncident: number;
}

export interface ActivityItem {
  id: string;
  type: 'task' | 'document' | 'meeting' | 'comment' | 'milestone' | 'other';
  action: string;
  description: string;
  user: {
    id: string;
    name: string;
    avatar?: string;
  };
  project?: {
    id: string;
    name: string;
  };
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface Deadline {
  id: string;
  title: string;
  type: 'task' | 'milestone' | 'deliverable' | 'contract';
  dueDate: string;
  status: 'on_track' | 'at_risk' | 'overdue';
  priority: 'high' | 'medium' | 'low';
  project?: {
    id: string;
    name: string;
  };
  assignee?: {
    id: string;
    name: string;
  };
}

// Dashboard API endpoints
export const dashboardApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Get widget data - supports both org and project scope
    getWidgetData: builder.query<WidgetData, WidgetDataParams>({
      query: ({ key, params = {}, projectId }) => ({
        url: `/dashboards/widget-data/${key}${projectId ? `/${projectId}` : ''}`,
        params: {
          ...params,
        },
      }),
      providesTags: (_, __, { key, projectId }) => [
        { type: 'WidgetData', id: createProjectCacheKey(key, projectId) },
      ],
      transformResponse: (response: ApiResponse<WidgetData>) => response.data,
    }),

    // Get organization dashboard stats (no projectId)
    getOrgDashboardStats: builder.query<DashboardStats, void>({
      query: () => '/dashboards/widget-data/org-stats',
      providesTags: [{ type: 'WidgetData', id: 'org-stats' }],
      transformResponse: (response: ApiResponse<DashboardStats>) => response.data,
    }),

    // Get project dashboard stats (requires projectId from Redux)
    getProjectDashboardStats: builder.query<ProjectDashboardStats, { projectId: string }>({
      query: ({ projectId }) => `/dashboards/widget-data/project-stats/${projectId}`,
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: `project-stats_${projectId}` },
      ],
      transformResponse: (response: ApiResponse<ProjectDashboardStats>) => response.data,
    }),

    // Get recent activity - supports both scopes
    getRecentActivity: builder.query<any[], { projectId?: string; limit?: number }>({
      query: ({ projectId, limit = 10 }) => ({
        url: '/dashboards/widget-data/activity',
        params: {
          ...(projectId && { projectId }),
          limit,
        },
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: createProjectCacheKey('recent-activity', projectId) },
      ],
      transformResponse: (response: ApiResponse<any[]>) => response.data,
    }),

    // Get top projects (org scope only)
    getTopProjects: builder.query<
      {
        id: string;
        name: string;
        status: string;
        location: string;
        dueDate: string | null;
        progress: number;
      }[],
      { limit?: number }
    >({
      query: ({ limit = 5 }) => ({
        url: '/dashboards/widget-data/top-projects',
        params: { limit },
      }),
      providesTags: [{ type: 'WidgetData', id: 'top-projects' }],
      transformResponse: (
        response: ApiResponse<
          {
            id: string;
            name: string;
            status: string;
            location: string;
            dueDate: string | null;
            progress: number;
          }[]
        >
      ) => response.data,
    }),

    // Get project overview (project scope only)
    getProjectOverview: builder.query<any, { projectId: string }>({
      query: ({ projectId }) => `/dashboards/widget-data/project-stats/${projectId}`,
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: `project-overview_${projectId}` },
      ],
      transformResponse: (response: ApiResponse<any>) => response.data,
    }),

    // Get financial summary - supports both scopes
    getFinancialSummary: builder.query<any, { projectId?: string; period?: string }>({
      query: ({ projectId, period = 'month' }) => ({
        url: '/dashboards/widget-data/financial',
        params: {
          ...(projectId && { projectId }),
          period,
        },
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: createProjectCacheKey('financial-summary', projectId) },
      ],
      transformResponse: (response: ApiResponse<any>) => response.data,
    }),

    // Get task summary - project scope required
    getTaskSummary: builder.query<any, { projectId?: string }>({
      query: ({ projectId }) =>
        projectId
          ? `/dashboards/widget-data/task-summary/${projectId}`
          : '/dashboards/widget-data/org-stats',
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: createProjectCacheKey('task-summary', projectId) },
      ],
      transformResponse: (response: ApiResponse<any>) => response.data,
    }),

    // Get upcoming deadlines - supports both scopes
    getUpcomingDeadlines: builder.query<any[], { projectId?: string; limit?: number }>({
      query: ({ projectId, limit = 5 }) => ({
        url: '/dashboards/widget-data/deadlines',
        params: {
          ...(projectId && { projectId }),
          limit,
        },
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: createProjectCacheKey('upcoming-deadlines', projectId) },
      ],
      transformResponse: (response: ApiResponse<any[]>) => response.data,
    }),

    // Get team performance - supports both scopes
    getTeamPerformance: builder.query<any, { projectId?: string; period?: string }>({
      query: ({ projectId, period = 'week' }) => ({
        url: '/dashboards/widget-data/team-performance',
        params: {
          ...(projectId && { projectId }),
          period,
        },
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: createProjectCacheKey('team-performance', projectId) },
      ],
      transformResponse: (response: ApiResponse<any>) => response.data,
    }),

    // Budget summary - supports both scopes
    getBudgetSummary: builder.query<BudgetSummary, { projectId?: string }>({
      query: ({ projectId }) => ({
        url: '/dashboards/widget-data/budget-summary',
        params: { ...(projectId && { projectId }) },
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: createProjectCacheKey('budget-summary', projectId) },
      ],
      transformResponse: (response: ApiResponse<BudgetSummary>) => response.data,
    }),

    // Schedule summary - project scope
    getScheduleSummary: builder.query<ScheduleSummary, { projectId: string }>({
      query: ({ projectId }) => `/dashboards/widget-data/schedule-summary/${projectId}`,
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: `schedule-summary_${projectId}` },
      ],
      transformResponse: (response: ApiResponse<ScheduleSummary>) => response.data,
    }),

    // Team summary - supports both scopes
    getTeamSummary: builder.query<TeamSummary, { projectId?: string }>({
      query: ({ projectId }) => ({
        url: '/dashboards/widget-data/team-summary',
        params: { ...(projectId && { projectId }) },
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: createProjectCacheKey('team-summary', projectId) },
      ],
      transformResponse: (response: ApiResponse<TeamSummary>) => response.data,
    }),

    // Document summary - project scope
    getDocumentSummary: builder.query<DocumentSummary, { projectId: string }>({
      query: ({ projectId }) => `/dashboards/widget-data/document-summary/${projectId}`,
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: `document-summary_${projectId}` },
      ],
      transformResponse: (response: ApiResponse<DocumentSummary>) => response.data,
    }),

    // Safety summary - project scope
    getSafetySummary: builder.query<SafetySummary, { projectId: string }>({
      query: ({ projectId }) => `/dashboards/widget-data/safety-summary/${projectId}`,
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: `safety-summary_${projectId}` },
      ],
      transformResponse: (response: ApiResponse<SafetySummary>) => response.data,
    }),

    // Activity feed - supports both scopes with pagination
    getActivityFeed: builder.query<
      { activities: ActivityItem[]; total: number },
      { projectId?: string; limit?: number; offset?: number }
    >({
      query: ({ projectId, limit = 20, offset = 0 }) => ({
        url: '/dashboards/widget-data/activity',
        params: { ...(projectId && { projectId }), limit, offset },
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: createProjectCacheKey('activity', projectId) },
      ],
      transformResponse: (response: ApiResponse<{ activities: ActivityItem[]; total: number }>) =>
        response.data,
    }),

    // Deadlines/Milestones - supports both scopes
    getDeadlines: builder.query<Deadline[], { projectId?: string; limit?: number }>({
      query: ({ projectId, limit = 10 }) => ({
        url: '/dashboards/widget-data/deadlines',
        params: { ...(projectId && { projectId }), limit },
      }),
      providesTags: (_, __, { projectId }) => [
        { type: 'WidgetData', id: createProjectCacheKey('deadlines', projectId) },
      ],
      transformResponse: (response: ApiResponse<Deadline[]>) => response.data,
    }),

    // Dashboard CRUD operations
    getDashboards: builder.query<ApiResponse<Dashboard[]>, GetDashboardsParams | void>({
      query: params => ({
        url: '/dashboards',
        params: params || {},
      }),
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: 'Dashboards' as const, id: _id })),
              { type: 'Dashboards', id: 'LIST' },
            ]
          : [{ type: 'Dashboards', id: 'LIST' }],
    }),

    getDashboard: builder.query<ApiResponse<Dashboard>, string>({
      query: id => `/dashboards/${id}`,
      providesTags: (result, error, id) => [{ type: 'Dashboard', id }],
    }),

    createDashboard: builder.mutation<ApiResponse<Dashboard>, CreateDashboardRequest>({
      query: data => ({
        url: '/dashboards',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'Dashboards', id: 'LIST' },
        { type: 'UserPrefs', id: 'DEFAULT' },
      ],
    }),

    updateDashboard: builder.mutation<
      ApiResponse<Dashboard>,
      { id: string; data: UpdateDashboardRequest }
    >({
      query: ({ id, data }) => ({
        url: `/dashboards/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Dashboard', id },
        { type: 'Dashboards', id: 'LIST' },
      ],
    }),

    deleteDashboard: builder.mutation<ApiResponse<void>, string>({
      query: id => ({
        url: `/dashboards/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Dashboard', id },
        { type: 'Dashboards', id: 'LIST' },
        { type: 'UserPrefs', id: 'DEFAULT' },
      ],
    }),

    // Widget operations
    getWidgets: builder.query<ApiResponse<DashboardWidget[]>, string>({
      query: dashboardId => `/dashboards/${dashboardId}/widgets`,
      providesTags: (result, error, dashboardId) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: 'Widgets' as const, id: _id })),
              { type: 'Widgets', id: dashboardId },
            ]
          : [{ type: 'Widgets', id: dashboardId }],
    }),

    createWidget: builder.mutation<
      ApiResponse<DashboardWidget>,
      { dashboardId: string; data: CreateWidgetRequest }
    >({
      query: ({ dashboardId, data }) => ({
        url: `/dashboards/${dashboardId}/widgets`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { dashboardId }) => [
        { type: 'Widgets', id: dashboardId },
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    updateWidget: builder.mutation<
      ApiResponse<DashboardWidget>,
      { id: string; data: UpdateWidgetRequest; dashboardId?: string }
    >({
      query: ({ id, data }) => ({
        url: `/dashboards/widgets/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, _error, { id, dashboardId }) => {
        const tags: Array<{ type: 'Widget' | 'Widgets'; id: string }> = [{ type: 'Widget', id }];
        // Invalidate specific dashboard widgets if known, otherwise invalidate all
        if (dashboardId) {
          tags.push({ type: 'Widgets', id: dashboardId });
        }
        // Also use the widget ID to invalidate (matches getWidgets providesTags)
        tags.push({ type: 'Widgets', id });
        return tags;
      },
    }),

    deleteWidget: builder.mutation<ApiResponse<void>, { id: string; dashboardId?: string }>({
      query: ({ id }) => ({
        url: `/dashboards/widgets/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id, dashboardId }) => {
        const tags: Array<{ type: 'Widget' | 'Widgets'; id: string }> = [
          { type: 'Widget', id },
          { type: 'Widgets', id },
        ];
        if (dashboardId) {
          tags.push({ type: 'Widgets', id: dashboardId });
        }
        return tags;
      },
    }),

    updateLayout: builder.mutation<
      ApiResponse<void>,
      { dashboardId: string; data: UpdateLayoutRequest }
    >({
      query: ({ dashboardId, data }) => ({
        url: `/dashboards/${dashboardId}/layout`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { dashboardId }) => [
        { type: 'Widgets', id: dashboardId },
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    // User preferences
    getDefaultDashboard: builder.query<ApiResponse<{ defaultDashboardId: string | null }>, string>({
      query: userId => `/dashboards/users/${userId}/default-dashboard`,
      providesTags: [{ type: 'UserPrefs', id: 'DEFAULT' }],
    }),

    setDefaultDashboard: builder.mutation<
      ApiResponse<void>,
      { userId: string; data: SetDefaultDashboardRequest }
    >({
      query: ({ userId, data }) => ({
        url: `/dashboards/users/${userId}/default-dashboard`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: 'UserPrefs', id: 'DEFAULT' }],
    }),
  }),
  overrideExisting: false,
});

// Export hooks for use in components
export const {
  // Widget data hooks (existing)
  useGetWidgetDataQuery,
  useGetOrgDashboardStatsQuery,
  useGetProjectDashboardStatsQuery,
  useGetRecentActivityQuery,
  useGetTopProjectsQuery,
  useGetProjectOverviewQuery,
  useGetFinancialSummaryQuery,
  useGetTaskSummaryQuery,
  useGetUpcomingDeadlinesQuery,
  useGetTeamPerformanceQuery,

  // New widget data hooks
  useGetBudgetSummaryQuery,
  useGetScheduleSummaryQuery,
  useGetTeamSummaryQuery,
  useGetDocumentSummaryQuery,
  useGetSafetySummaryQuery,
  useGetActivityFeedQuery,
  useGetDeadlinesQuery,

  // Dashboard CRUD hooks
  useGetDashboardsQuery,
  useGetDashboardQuery,
  useLazyGetDashboardQuery,
  useCreateDashboardMutation,
  useUpdateDashboardMutation,
  useDeleteDashboardMutation,

  // Widget management hooks
  useGetWidgetsQuery,
  useLazyGetWidgetsQuery,
  useCreateWidgetMutation,
  useUpdateWidgetMutation,
  useDeleteWidgetMutation,
  useUpdateLayoutMutation,

  // User preferences hooks
  useGetDefaultDashboardQuery,
  useLazyGetDefaultDashboardQuery,
  useSetDefaultDashboardMutation,
} = dashboardApi;

// Export endpoints for manual cache invalidation or prefetching
export const {
  getDashboards,
  getDashboard,
  createDashboard,
  updateDashboard,
  deleteDashboard,
  getWidgets,
  createWidget,
  updateWidget,
  deleteWidget,
  updateLayout,
  getDefaultDashboard,
  setDefaultDashboard,
} = dashboardApi.endpoints;

// Utility functions for cache management
export const dashboardApiUtils = {
  // Invalidate all dashboard data
  invalidateAll: (dispatch: any) => {
    dispatch(
      dashboardApi.util.invalidateTags([
        'Dashboards',
        'Dashboard',
        'Widgets',
        'Widget',
        'UserPrefs',
      ])
    );
  },

  // Invalidate specific dashboard and its widgets
  invalidateDashboard: (dispatch: any, dashboardId: string) => {
    dispatch(
      dashboardApi.util.invalidateTags([
        { type: 'Dashboard', id: dashboardId },
        { type: 'Widgets', id: dashboardId },
      ])
    );
  },

  // Prefetch dashboard data
  prefetchDashboard: (dispatch: any, dashboardId: string) => {
    dispatch(getDashboard.initiate(dashboardId));
    dispatch(getWidgets.initiate(dashboardId));
  },

  // Reset all queries (useful on logout)
  resetApiState: (dispatch: any) => {
    dispatch(dashboardApi.util.resetApiState());
  },
};
